import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Cookies from 'universal-cookie';
import classNames from 'classnames';
import {
  validateCorporateAndValidEmail,
  validatePhone,
  identifyHubspotUser,
} from '../../shared/helpers/forms';
import { OrganizationType } from '../../shared/helpers/enum';
import { request } from '../../shared/apis/utils';
import { sendEvent } from '../../lib/gtm.js';
import { intercomHidden, submitHubspotForm, getNameParts } from './common/helpers';
import Title from './common/Title';
import SubmitButton from './common/SubmitButton';

import TextInput from '../Form/Input/TextInput';

import Styles from './Styles.module.scss';

const cookies = new Cookies();

const EnterpriseRegistration = ({ query, ...props }) => {
  const router = useRouter();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');

  const [submitted, setSubmitted] = useState(false);
  const [emailValid, setEmailValid] = useState(true);
  const [phoneValid, setPhoneValid] = useState(true);
  const [processed, setProcessed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitConfirm, setSubmitConfirm] = useState(false);
  const [messageType, setMessageType] = useState('');
  const [uuid, setUuid] = useState('');
  const [corporateValid, setCorporateValid] = useState(true);
  const [messageSent, setMessageSent] = useState(false);
  const [showCompanyLogin, setShowCompanyLogin] = useState(false);

  useEffect(() => {
    intercomHidden();
  }, []);

  useEffect(() => {
    if (Object.keys(router.query).length > 0) {
      setFullName(router.query.name);
      setEmail(router.query.email);
      setCompany(router.query.company);
      setPhone(router.query.phone);
    }
  }, [router.query]);

  const callMethod = (name, value) => {
    const alias = {
      fullNameSetter: setFullName,
      emailSetter: setEmail,
      companySetter: setCompany,
      phoneSetter: setPhone,
      phoneValid: setPhoneValid,
      emailValid: setEmailValid,
      corporateValid: setCorporateValid,
    };
    alias[name](value);
  };

  const handleChange = (event) => {
    setPhoneValid(true);
    setProcessed(false);

    const { name, value } = event.target;
    if (name === 'email') {
      setEmailValid(true);
      setCorporateValid(true);
    }
    callMethod(`${name}Setter`, value);
  };

  const checkEmail = (email) => {
    const validator = validateCorporateAndValidEmail(email);
    setEmailValid(validator.valid);
    setCorporateValid(validator.corporate);
    return validator.valid;
  };

  const checkPhone = (phone) => {
    const validPhone = validatePhone(phone);
    setPhoneValid(validPhone);
    return validPhone;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setSubmitted(true);

    const pageUrl = window.location.href;
    const pageName = document.title;

    const isEmailValid = !!email && checkEmail(email);
    const isPhoneValid = !!phone && checkPhone(phone);

    if (isEmailValid && fullName && company && phone && isPhoneValid) {
      setLoading(true);
      setShowCompanyLogin(false);
      const { firstName, lastName } = getNameParts(fullName);

      const data = {
        name: company,
        user_first_name: firstName,
        user_last_name: lastName,
        user_email: email,
        phone_number: phone,
        type: OrganizationType.ENTERPRISE.id,
        site: process.env.NEXT_PUBLIC_SITE_ID,
      };

      identifyHubspotUser({
        email,
        name: fullName,
        company,
        phone_number: phone,
        type: OrganizationType.ENTERPRISE.id,
      });
      sendEvent({ event: 'GTM-EntLeadGen' });

      // Identify User Block
      try {
        const hubspotData = {
          ...query,
          fullName,
          email,
          company,
          phone,
          work_email: email,
          site: process.env.NEXT_PUBLIC_SITE_NAME,
        };
        console.log(hubspotData);
        submitHubspotForm(
          process.env.NEXT_PUBLIC_ENTERPRISE_FORMID,
          pageUrl,
          pageName,
          hubspotData
        );
      } catch (err) {
        console.error(err);
      }

      request
        .post(`${process.env.NEXT_PUBLIC_ONBOARD_URL}/`, data)
        .then((response) => {
          if (response.data) {
            handleResponse(response.data);
          }
        })
        .catch((error) => {
          if (error.response) {
            const data = error.response.data;
            if (data) {
              if (data.nonFieldErrors && data.nonFieldErrors.length) {
                handleError(error.response.data.nonFieldErrors[0]);
              } else if (error.response.data.userEmail && error.response.data.userEmail.length) {
                handleError(error.response.data.userEmail[0]);
              }
            }
          }
        });
    }
  };

  const handleResponse = (response) => {
    sendEvent({ event: 'GTM-EntLeadGenConfirm' });

    if (response.uuid) {
      cookies.set('uuid', response.uuid, { path: '/' });
      setUuid(response.uuid);

      setFullName('');
      setEmail('');
      setCompany('');
      setSubmitConfirm(true);
      setSubmitted(false);
      setPhone('');
      setLoading(false);
      setMessageSent(true);

      setTimeout(() => {
        setLoading(false);
        setMessageSent(false);
      }, 5000);
    }
  };

  const handleError = (error) => {
    if (error === 'A user is already registered with this email address.') {
      setShowCompanyLogin(true);
      setProcessed(true);
    }
    setSubmitted(false);
    setProcessed(true);
    setMessageType('error');
    setMessageType(error);
    setLoading(false);
  };

  const handleEmailBlur = (event) => {
    checkEmail(email);
  };

  let successMessage;
  const validateEmail = () => {
    if (submitted && !email) {
      return 'Enter your email address';
    } else if (email) {
      if (processed) {
        return `You have an existing account with ${process.env.NEXT_PUBLIC_SITE_NAME}`;
      } else if (!emailValid) {
        return 'Enter a valid email address';
      } else if (!corporateValid) {
        return 'Please enter your company email address if available';
      }
    }
  };

  const validatePhoneNumber = () => {
    if (submitted && !phone) {
      return 'Enter your phone number';
    } else if (phone && !phoneValid) {
      return 'Enter a valid phone number';
    }
  };

  if (messageSent) {
    successMessage = (
      <div className={classNames(Styles.enquiryMessageSuccess, Styles.cLeadgenSuccess)}>
        {'Thank you. A member of the sales team will be in touch shortly.'}
      </div>
    );
  }

  return (
    <div>
      <div className={Styles.form}>
        <Title
          title='Work with the best talents and teams on your most important projects.'
          subtitle={`Quality guaranteed. Get in touch with ${process.env.NEXT_PUBLIC_SITE_NAME} today.`}
        />
        <div className={Styles.inputContainer}>
          <TextInput
            name='fullName'
            label='Full Name'
            error={submitted && !fullName && 'Enter your full name'}
            value={fullName}
            onKeyPress={handleKeyPress}
            onChange={handleChange}
          />
        </div>
        <div className={Styles.inputContainer}>
          <TextInput
            name='email'
            label='Company Email Address'
            className={classNames({
              [Styles.corporateNote]: emailValid && !processed && !corporateValid,
            })}
            error={validateEmail()}
            value={email}
            onKeyPress={handleKeyPress}
            onBlur={handleEmailBlur}
            onChange={handleChange}
          />
          {showCompanyLogin && (
            <div className={Styles.inputAltAction}>
              <a
                className={Styles.errorMsgLink}
                title=''
                href={`${process.env.NEXT_PUBLIC_APP_LOGIN_URL}?email=${email}`}>
                Login &rarr;
              </a>
            </div>
          )}
        </div>
        <div className={Styles.inputContainer}>
          <TextInput
            name='company'
            label='Company Name'
            error={submitted && !company && 'Enter your company name'}
            value={company}
            onKeyPress={handleKeyPress}
            onChange={handleChange}
          />
        </div>
        <div className={Styles.inputContainer}>
          <TextInput
            name='phone'
            label='Phone Number'
            error={validatePhoneNumber()}
            value={phone}
            onKeyPress={handleKeyPress}
            onChange={handleChange}
          />
        </div>

        {successMessage}

        {!messageSent && (
          <div className={Styles.formButton}>
            <SubmitButton onSubmit={handleSubmit} loading={loading}>
              {'Schedule A Call'}
            </SubmitButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnterpriseRegistration;
